import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomainChoice, shouldUsePlatformDefault } from '@galaxy/email-ui/domain-chooser';
import { TranslateService } from '@galaxy/lexicon';
import { UserService } from '@vendasta/business-center';
import { Sender, SenderType } from '@vendasta/email';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { IAMService } from '@vendasta/iamv2';
import { UserIdentifier } from '@vendasta/iamv2/lib/_internal/objects/api';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { MarketsService } from '../../core/markets/markets.service';
import { PreviewEmailModalComponent } from '../user/preview-email/preview-email-modal.component';

export interface ResendWelcomeEmailDialogData {
  userID: string;
  partnerID: string;
  marketId?: string;
  loginUrl: string;
  userLanguage?: string;
}

@Component({
  templateUrl: 'resend-welcome-email-dialog.component.html',
  styleUrls: ['resend-welcome-email-dialog.component.scss'],
  standalone: false,
})
export class ResendWelcomeEmailDialogComponent implements OnInit {
  form: UntypedFormGroup;
  marketId: string;
  sender: Sender;
  marketName$: Observable<string>;

  constructor(
    private dialogRef: MatDialogRef<ResendWelcomeEmailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: ResendWelcomeEmailDialogData,
    private bcUserService: UserService,
    private formBuilder: UntypedFormBuilder,
    private snackService: SnackbarService,
    private translationService: TranslateService,
    private iamService: IAMService,
    private marketService: MarketsService,
    public previewModal: MatDialog,
  ) {}

  ngOnInit(): void {
    this.marketId = this.data.marketId;
    this.form = this.formBuilder.group({
      sendFromDefault: this.formBuilder.control('partner-domain', [Validators.required]),
    });
    this.marketName$ = this.marketService.getMarket(this.data?.marketId || 'default').pipe(
      map((market) => {
        return market?.name || 'default';
      }),
    );
    this.sender = new Sender({ type: SenderType.SENDER_TYPE_PARTNER, id: this.data.partnerID });
  }

  resendWelcomeEmail(choice: DomainChoice): void {
    this.bcUserService
      .sendWelcomeEmail(
        this.data.partnerID,
        this.data.userID,
        this.data.loginUrl,
        shouldUsePlatformDefault(choice),
        this.marketId,
      )
      .subscribe({
        next: () => {
          this.snackService.successSnack(
            this.translationService.instant('USERS.RESEND_WELCOME_EMAIL_MODAL.SUCCESS_MESSAGE'),
          );
          this.dialogRef.close();
        },
        error: () =>
          this.snackService.errorSnack(
            this.translationService.instant('USERS.RESEND_WELCOME_EMAIL_MODAL.ERROR_MESSAGE'),
          ),
      });
  }

  async openPreviewEmail(): Promise<void> {
    const user = await firstValueFrom(this.iamService.getUser({ userId: this.data.userID } as UserIdentifier));

    this.previewModal.open(PreviewEmailModalComponent, {
      data: {
        template: this.bcUserService.getWelcomeEmailTemplate(
          this.data.partnerID,
          user.greetingName || user.firstName || '',
          this.data.loginUrl,
          this.marketId,
          '',
          user.languageCode || 'en',
        ),
      },
      width: '620px',
      height: '80vh',
      maxHeight: '800px',
    });
  }
}
